// ═══════════════════════════════════════════════════════════════
// ClearView CRM Field Mapper — camelCase ↔ snake_case conversion
// ═══════════════════════════════════════════════════════════════

/** Convert a camelCase string to snake_case */
const camelToSnake = (str) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

/** Convert a snake_case string to camelCase */
const snakeToCamel = (str) =>
  str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

// Fields that should NOT be mapped (already correct or special)
const SKIP_FIELDS = new Set([
  "id",
  "email",
  "phone",
  "website",
  "birthdate",
  "cfre",
  "designation",
  "chapter",
  "organization",
  "notes",
  "pathway",
  "ethnicity",
]);

// Fields that start with "custom_" are stored in custom_fields JSONB
const isCustomField = (key) => key.startsWith("custom_");

/**
 * Convert a JS member object (camelCase) to DB format (snake_case)
 * Extracts custom_* fields into a custom_fields JSONB object
 */
export const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  const result = {};
  const customFields = {};

  Object.entries(obj).forEach(([key, value]) => {
    // Skip internal React/UI-only fields
    if (key === "contactLog") return;

    // Collect custom fields into JSONB
    if (isCustomField(key)) {
      customFields[key] = value;
      return;
    }

    const snakeKey = SKIP_FIELDS.has(key) ? key : camelToSnake(key);
    result[snakeKey] = value;
  });

  // Only add custom_fields if there are any
  if (Object.keys(customFields).length > 0) {
    result.custom_fields = customFields;
  }

  return result;
};

/**
 * Convert a DB row (snake_case) to JS member object (camelCase)
 * Spreads custom_fields JSONB back into top-level custom_* keys
 */
export const toCamelCase = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  const result = {};

  Object.entries(obj).forEach(([key, value]) => {
    // Spread custom_fields JSONB back to top-level
    if (key === "custom_fields" && value && typeof value === "object") {
      Object.assign(result, value);
      return;
    }

    // Handle nested arrays (contact_logs from join)
    if (key === "contact_logs" && Array.isArray(value)) {
      result.contactLog = value.map(toCamelCase);
      return;
    }

    const camelKey = SKIP_FIELDS.has(key) ? key : snakeToCamel(key);
    result[camelKey] = value;
  });

  return result;
};

/**
 * Convert an array of DB rows to camelCase
 */
export const toCamelCaseArray = (arr) =>
  Array.isArray(arr) ? arr.map(toCamelCase) : [];
